/**
 * Custom hook for unified error handling in React components
 * Provides consistent error state management and display
 */
import { useState } from 'react';

export const useError = () => {
  const [error, setError] = useState(null);
  const [isError, setIsError] = useState(false);

  /**
   * Show error with structured format
   * @param {string|object} errorInput - Error message or error object from API
   * @param {object} context - Additional context for debugging (client-side only)
   */
  const showError = (errorInput, context = {}) => {
    let errorData;
    
    if (typeof errorInput === 'string') {
      // Simple string error
      errorData = {
        message: errorInput,
        code: 'CLIENT_ERROR',
        details: context
      };
    } else if (errorInput?.type && errorInput?.message && errorInput?.code) {
      // Already processed error object from processApiError
      errorData = {
        message: errorInput.message,
        code: errorInput.code,
        details: errorInput.details || {},
        requestId: errorInput.requestId,
        type: errorInput.type,
        level: errorInput.level
      };
    } else if (errorInput?.response?.data?.error) {
      // API error response from backend
      errorData = {
        message: errorInput.response.data.error.message || 'An error occurred',
        code: errorInput.response.data.error.code || 'API_ERROR',
        details: errorInput.response.data.error.details || {},
        requestId: errorInput.response.data.error.request_id
      };
    } else if (errorInput?.message) {
      // JavaScript Error object
      errorData = {
        message: errorInput.message,
        code: 'JS_ERROR',
        details: {
          name: errorInput.name,
          stack: process.env.NODE_ENV === 'development' ? errorInput.stack : undefined,
          ...context
        }
      };
    } else {
      // Unknown error format
      errorData = {
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        details: { originalError: errorInput, ...context }
      };
    }

    setError(errorData);
    setIsError(true);
    
    // Log error for debugging (client-side only, no sensitive data)
    if (process.env.NODE_ENV === 'development') {
      console.error('[Error Handler]', {
        message: errorData.message,
        code: errorData.code,
        requestId: errorData.requestId,
        timestamp: new Date().toISOString()
      });
    }
  };

  /**
   * Clear current error state
   */
  const clearError = () => {
    setError(null);
    setIsError(false);
  };

  /**
   * Show a network error with retry suggestion
   * @param {Error} networkError - Network error object
   */
  const showNetworkError = (networkError) => {
    showError({
      message: 'Network connection failed. Please check your connection and try again.',
      code: 'NETWORK_ERROR',
      details: {
        originalMessage: networkError.message,
        isNetworkError: true
      }
    });
  };

  /**
   * Show a validation error for form fields
   * @param {string} message - Validation error message
   * @param {string} field - Field name that failed validation
   */
  const showValidationError = (message, field) => {
    const errorData = {
      message,
      code: 'VALIDATION_ERROR',
      details: { field, isValidationError: true }
    };
    
    setError(errorData);
    setIsError(true);
    
    if (process.env.NODE_ENV === 'development') {
      console.error('[Error Handler]', {
        message: errorData.message,
        code: errorData.code,
        timestamp: new Date().toISOString()
      });
    }
  };

  /**
   * Show an authentication error
   * @param {string} message - Auth error message
   */
  const showAuthError = (message = 'Authentication failed. Please login again.') => {
    const errorData = {
      message,
      code: 'AUTH_ERROR',
      details: { isAuthError: true }
    };
    
    setError(errorData);
    setIsError(true);
    
    if (process.env.NODE_ENV === 'development') {
      console.error('[Error Handler]', {
        message: errorData.message,
        code: errorData.code,
        timestamp: new Date().toISOString()
      });
    }
  };

  return {
    error,
    isError,
    showError,
    clearError,
    showNetworkError,
    showValidationError,
    showAuthError
  };
};